import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import {anchorElementId, novaInstanceKey, NovaAgentPicker, useIsPickerOpen, type NovaAgentPickerAlign} from './NovaAgentPicker';
import {showChangeAgentConfirmationModal} from './modals/ChangeAgentConfirmationModal';
import {NovaCompactButton} from './NovaCompactButton';
import {closeModal} from '../modals/generic_modal_conductor';

// Changing the agent is two separate things that only share a name, and they are kept apart here
// because they belong in different places on screen and have different lifetimes.
//
// The list is the long-lived half. The task modal's header caret opens it - that is where the
// design puts every agent list in this modal - so it has to be mounted whenever the header might
// offer that caret: while the thread is folded, while a run is in flight, on a turn that failed.
// Mounting it beside the button meant it came and went with whatever card row happened to hold the
// button, and a caret aimed at a list that is not mounted does nothing at all.
//
// The button is the short-lived half. It belongs in one card's action row, and only on a turn that
// has an answer to run a different agent against.

export interface ChangeAgentPickerProps {
	taskId: string;
	// See NovaAgentPickerProps.instanceKey (NovaAgentPicker.tsx). The key the trigger below toggles.
	instanceKey?: string;
	// See NovaAgentPickerProps.anchorInstanceKey: which control the list hangs from, when that is
	// not the trigger. The design opens it under the task modal's own Action/Continue control.
	anchorInstanceKey?: string;
	// How the list lines up with that control — 'right' where it sits at the edge of the modal.
	align?: NovaAgentPickerAlign;
	currentAgentId: string;
	hasReplies: boolean;
	// Whether this person may act on the agent at all. Checked here rather than only on the button,
	// so anything that opens the list another way still meets the refusal.
	canAct?: boolean;
	onAgentChanged: (newAgentId: string) => void;
}

export const ChangeAgentPicker = (props: ChangeAgentPickerProps): JSX.Element => {
	// Whether the confirmation this picker raised is still on screen. The dialog lives in the
	// modal conductor rather than in this tree, so it outlives the picker unless somebody closes
	// it - and the picker goes the moment the person loses the permission to change the agent,
	// which would otherwise leave them deciding about something they may no longer do.
	const confirmOpenRef = useRef(false);
	useEffect(
		() => () => {
			if (confirmOpenRef.current) {
				confirmOpenRef.current = false;
				closeModal();
			}
		},
		[]
	);

	const handleSelect = (selectedAgentId: string): void => {
		if (props.canAct === false) return;
		if (selectedAgentId === props.currentAgentId) return;
		if (props.hasReplies) {
			confirmOpenRef.current = true;
			showChangeAgentConfirmationModal({
				onConfirm: () => props.onAgentChanged(selectedAgentId),
				onClose: () => {
					confirmOpenRef.current = false;
				},
			});
		} else {
			props.onAgentChanged(selectedAgentId);
		}
	};

	return (
		<NovaAgentPicker
			taskId={props.taskId}
			instanceKey={props.instanceKey}
			anchorInstanceKey={props.anchorInstanceKey}
			align={props.align}
			onSelectAgent={handleSelect}
		/>
	);
};

export interface ChangeAgentTriggerProps {
	taskId: string;
	// The key this opens. Must match the picker's own, which is what listens for the toggle.
	instanceKey?: string;
	// Shown, but refused, with the reason on it — changing the agent starts a fresh run, so it
	// needs the same permission acting on the task needs. The picker refuses on the same question;
	// this only keeps a control that cannot act from looking like one that can.
	disabled?: boolean;
	disabledReason?: string;
}

export const ChangeAgentTrigger = (props: ChangeAgentTriggerProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const open = useIsPickerOpen(props.taskId, props.instanceKey);

	return (
		// The same compact secondary control the answer card's other actions use: changing the agent
		// is not the card's primary act, and a text link among bordered buttons reads as a different
		// kind of thing than it is.
		//
		// The chevron sits on the right and turns when the list opens, which is how every other
		// control in this feature states that it owns a list - the row caret and the modal header's
		// do the same. It replaces the rotated sort glyph this button used to draw, which had to
		// stand in for a swap icon the design system does not ship.
		<NovaCompactButton
			tone="secondary"
			rightIcon={open ? 'chevronUp' : 'chevronDown'}
			onPress={() => dispatch(EVENT_ID.NOVA_TOGGLE_PICKER, novaInstanceKey(props.taskId, props.instanceKey))}
			disabled={props.disabled}
			title={props.disabled ? props.disabledReason : undefined}
			data-cy="nova-change-agent"
			id={anchorElementId(props.taskId, props.instanceKey)}
		>
			{formatMessage({id: 'nova_invocation.change_agent.button'})}
		</NovaCompactButton>
	);
};
